import { css, type Theme } from '@emotion/react';

import { withOpacity } from '@hrworks/design-system/functions';
import { mq, overflowHyphens } from '@hrworks/design-system/utilities';
import { legacyFontSizeBySizeMap } from '@hrworks/sui-shared/functions';

import { getButtonColorMap } from './Button.colors';
import type { ButtonProps } from './Button.types';

export type ButtonBaseStylesParams = {
  theme: Theme;
  withTypography?: boolean;
} & Partial<Pick<ButtonProps, 'size' | 'corner' | 'textAlign' | 'width'>>;

export type ButtonVariantStylesParams = {
  theme: Theme;
  variant: NonNullable<ButtonProps['variant']>;
  color?: NonNullable<ButtonProps['color']>;
};

/**
 * Base layout styles shared by all button-like elements.
 * Display, alignment, padding, border-radius, typography, cursor.
 */
export const generateButtonBaseStyles = ({
  theme,
  size = 'medium',
  corner = 'rounded',
  width,
  textAlign,
  withTypography = true,
}: ButtonBaseStylesParams) =>
  css({
    ...(withTypography && theme.eclipseTier2Typography.labelMdSemibold),
    appearance: 'none',
    textAlign,
    textDecoration: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    border: 'none',
    gap: '0.571em',
    padding: '0.571em 0.857em',
    color: 'inherit',
    whiteSpace: 'nowrap',
    borderRadius:
      corner === 'rounded'
        ? theme.eclipseTier1.radius['8px']
        : corner === 'pill'
          ? theme.eclipseTier1.radius['9999px']
          : theme.eclipseTier1.radius['0px'],
    fontSize: legacyFontSizeBySizeMap[size],
    ...(width === 'full' && {
      display: 'flex',
      width: '100%',
      justifyContent: textAlign || 'center',
      flex: 1,
    }),
  });

/**
 * Focus-ring styles (outline on :focus-visible).
 */
export const generateInteractiveButtonStyles = (theme: Theme) =>
  css({
    '&&': {
      outline: '2px solid transparent',
    },
    outlineOffset: 2,
    ':focus-visible': {
      outlineColor: theme.eclipseTier2Color.border.focus,
      position: 'relative',
    },
  });

/**
 * Disabled styles.
 */
export const generateButtonDisabledStyles = (theme: Theme) =>
  css({
    ':disabled, &[aria-disabled="true"]': {
      opacity: theme.variables.opacity.disabled,
      cursor: 'not-allowed',
    },
  });

/**
 * Variant color styles (background, text color, hover, pressed).
 * Can be used inside selectors without specificity conflicts.
 */
export const generateButtonVariantStyles = ({ theme, variant, color = 'primary' }: ButtonVariantStylesParams) => {
  const buttonColorMap = getButtonColorMap(theme);
  const notDisabled = ':not(:disabled):not([aria-disabled="true"])';

  if (variant === 'text' || variant === 'link') {
    return css([
      overflowHyphens,
      {
        color: color === 'secondary' ? theme.eclipseTier2Color.text.default : buttonColorMap.filled[color].default,
        background: 'none',
        padding: 0,
        whiteSpace: 'wrap',

        [mq.supportsHover]: {
          [`${notDisabled}:hover`]: {
            color:
              color === 'secondary'
                ? withOpacity(theme.eclipseTier2Color.text.default, '75%')
                : buttonColorMap.filled[color].hovered,
          },
        },
      },
    ]);
  }

  if (variant === 'unstyled') {
    return css({});
  }

  if (variant === 'filled') {
    return css({
      backgroundColor: buttonColorMap.filled[color].default,
      color: buttonColorMap.filled[color].color,

      [notDisabled]: {
        [mq.supportsHover]: {
          ':hover': {
            backgroundColor: buttonColorMap.filled[color].hovered,
          },
        },

        ':active': {
          backgroundColor: buttonColorMap.filled[color].pressed,
        },
      },
    });
  }

  if (variant === 'ghost') {
    return css({
      backgroundColor: buttonColorMap.ghost[color].default,
      color: buttonColorMap.ghost[color].color,
      boxShadow: `inset 0px 0px 0px 2px ${buttonColorMap.ghost[color].color}`,

      [notDisabled]: {
        [mq.supportsHover]: {
          ':hover': {
            backgroundColor: buttonColorMap.ghost[color].hovered,
            ...(color === 'primary' && {
              color: theme.eclipseTier2Color.text.brand.hovered,
            }),
          },
        },

        ':active': {
          backgroundColor: buttonColorMap.ghost[color].pressed,
        },
      },
    });
  }

  if (variant === 'subtle') {
    return css({
      backgroundColor: buttonColorMap.subtle[color].default,
      color: buttonColorMap.subtle[color].color,

      [`&:hover${notDisabled}`]: {
        [mq.supportsHover]: {
          backgroundColor: buttonColorMap.subtle[color].hovered,
          ...(color === 'primary' && {
            color: theme.eclipseTier2Color.text.brand.hovered,
          }),
        },

        ':active': {
          backgroundColor: buttonColorMap.subtle[color].pressed,
          ...(color === 'primary' && {
            color: theme.eclipseTier2Color.text.brand.pressed,
          }),
        },
      },
    });
  }

  return css({});
};
